// Restore from a backup file into an EMPTY diary (Implementation Plan v02 §6.4,
// IR-34; IP2-36 (c); I-1a Batch 6b). Merge into a diary that already has
// records (newer `updated_at` wins, the other kept as a version) is I-1b.
//
// - Called only with contents that export/openBackup has already checked and
//   opened: nothing here runs for a damaged, altered or newer file.
// - Every record is sealed under the NEW phone's data key BEFORE the write
//   (IndexedDB transactions close on any non-IndexedDB await). Photo blobs are
//   copied as they are: each is still sealed under its own photo key, which is
//   inside its moment's record.
// - ONE transaction checks that the diary is empty and writes the keyring, the
//   records, the blobs and the data-schema version — all or nothing. A full disk
//   aborts it, so nothing is half-written.
// - Records keep the backup's data-schema version; opening the store afterwards
//   runs the migration runner as for any older diary.

import { type Bytes, type KeyWrap, PHOTO_KEY_ID, type SealedRecord } from '../crypto'

import { type DiaryRecord, encodeRecord } from './codec'
import { META_DATA_SCHEMA, type StoreDb, watchDone } from './schema'

/** A photo blob as carried in a backup: still sealed under its own photo key. */
export interface RestoreBlob {
  readonly id: string
  readonly schemaVersion: number
  readonly iv: Bytes
  readonly ct: Bytes
}

export interface RestoreInput {
  /** The new wraps of the new data key (app lock, optional recovery code). */
  readonly wraps: readonly KeyWrap[]
  readonly records: readonly DiaryRecord[]
  readonly blobs: readonly RestoreBlob[]
  /** Data-schema version of the records (from the backup header). */
  readonly schemaVersion: number
}

/** 'already-set-up' = a keyring exists; 'not-empty' = no keyring, but records or photos are left. */
export type RestoreOutcome = 'written' | 'already-set-up' | 'not-empty'

function sealedBlob(blob: RestoreBlob): SealedRecord {
  return { id: blob.id, kind: 'blob', schemaVersion: blob.schemaVersion, keyId: PHOTO_KEY_ID, iv: blob.iv, ct: blob.ct }
}

export async function restoreIntoEmpty(db: StoreDb, dataKey: CryptoKey, input: RestoreInput): Promise<RestoreOutcome> {
  const sealed: SealedRecord[] = []
  for (const record of input.records) {
    sealed.push(await encodeRecord(dataKey, record, input.schemaVersion))
  }

  const tx = db.transaction(['keyring', 'records', 'blobs', 'meta'], 'readwrite')
  const done = watchDone(tx)
  try {
    const keyring = tx.objectStore('keyring')
    const records = tx.objectStore('records')
    const blobs = tx.objectStore('blobs')
    const [keys, recordCount, blobCount] = await Promise.all([keyring.count(), records.count(), blobs.count()])
    if (keys > 0 || recordCount > 0 || blobCount > 0) {
      await done
      return keys > 0 ? 'already-set-up' : 'not-empty'
    }
    // Each request gets a handler as it is queued, so the ones aborted after a
    // later failure never surface as unhandled rejections.
    const pending: Promise<unknown>[] = []
    const queue = (request: Promise<unknown>): void => {
      request.catch(() => undefined)
      pending.push(request)
    }
    input.wraps.forEach((wrap) => queue(keyring.add(wrap)))
    sealed.forEach((record) => queue(records.add(record)))
    input.blobs.forEach((blob) => queue(blobs.add(sealedBlob(blob))))
    queue(tx.objectStore('meta').put(input.schemaVersion, META_DATA_SCHEMA))
    await Promise.all(pending)
    await done
    return 'written'
  } catch (error) {
    // A request that fails (or throws before it is queued) must not let the
    // requests already queued commit: all or nothing.
    try {
      tx.abort()
    } catch {
      // Already aborted (e.g. the browser's own "storage full" abort).
    }
    throw error
  }
}
